/**
 * AddButton Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 467-452
 *
 * A floating action button (FAB) for adding new items.
 * Features rounded and square variants with interactive states.
 * Supports rotation animation to transform Add icon into X icon.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Animated, GestureResponderEvent, Pressable, StyleSheet } from 'react-native';
import { AddIcon } from '../icons';
import { colors } from '../tokens/colors';

export interface AddButtonProps {
  /**
   * Button shape variant
   * @default 'rounded'
   */
  variant?: 'rounded' | 'square';

  /**
   * Callback when button is pressed
   */
  onPress?: (e: GestureResponderEvent) => void;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;

  /**
   * Active state - rotates icon to form X
   * @default false
   */
  active?: boolean;
}

export const AddButton: React.FC<AddButtonProps> = ({
  variant = 'rounded',
  onPress,
  disabled = false,
  active = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Animated value for rotation (0 = Add, 45 = X)
  const rotateAnim = useRef(new Animated.Value(active ? 45 : 0)).current;

  // Animate rotation when active state changes
  useEffect(() => {
    Animated.spring(rotateAnim, {
      toValue: active ? 45 : 0,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();
  }, [active, rotateAnim]);

  // Determine background color based on state using Crop Circle color tokens
  const getBackgroundColor = () => {
    if (disabled) {
      return colors.neutral[400]; // #B4B4B4
    }
    if (active) {
      return colors.orange[500]; // #FF5505 - brand-core (active state)
    }
    if (isPressed) {
      return colors.orange[500]; // #FF5505 - brand-core (pressed)
    }
    if (isHovered) {
      return colors.orange[400]; // #FF8433 - lighter orange (hover)
    }
    return colors.orange[500]; // #FF5505 - brand-core (default)
  };

  const backgroundColor = getBackgroundColor();
  const borderRadius = variant === 'rounded' ? 99 : 4;

  // Interpolate rotation value
  const rotation = rotateAnim.interpolate({
    inputRange: [0, 45],
    outputRange: ['0deg', '45deg'],
  });

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      onPressIn={() => !disabled && setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      onHoverIn={() => !disabled && setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      disabled={disabled}
      style={[
        styles.container,
        {
          backgroundColor,
          borderRadius,
          opacity: disabled ? 0.5 : 1,
        },
      ]}
    >
      <Animated.View
        style={[
          styles.iconContainer,
          {
            transform: [{ rotate: rotation }],
          },
        ]}
      >
        <AddIcon size={24} color={colors.brand.white} />
      </Animated.View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 56,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

AddButton.displayName = 'AddButton';